import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCalendarAlt, faTag, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import { getAnnouncementBySlug, announcements } from '../../data/announcements';
import ImageGallery from '../../components/ImageGallery';
import duyurularHeroImage from '../../assets/images/duyurular.jpg';
import './AnnouncementDetail.css';

const AnnouncementDetail = () => {
  const { slug } = useParams();
  const announcement = getAnnouncementBySlug(slug);

  if (!announcement) {
    return (
      <div className="announcement-detail">
        <div className="container" style={{ padding: '100px 20px', textAlign: 'center' }}>
          <h1>Duyuru Bulunamadı</h1>
          <p>Aradığınız duyuru bulunamadı.</p>
          <Link to="/duyurular" className="btn btn-primary">
            Duyurulara Dön
          </Link>
        </div>
      </div>
    );
  }

  // Find previous and next announcements
  const currentIndex = announcements.findIndex(a => a.slug === slug);
  const previousAnnouncement = currentIndex > 0 ? announcements[currentIndex - 1] : null;
  const nextAnnouncement = currentIndex < announcements.length - 1 ? announcements[currentIndex + 1] : null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": announcement.title,
    "datePublished": announcement.date,
    "author": {
      "@type": "Organization",
      "name": "ProgenX Academy"
    },
    "publisher": {
      "@type": "Organization",
      "name": "ProgenX Academy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://progenxacademy.com/logo.png"
      }
    },
    "description": announcement.excerpt,
    "image": announcement.images && announcement.images.length > 0 ? announcement.images[0] : undefined,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://progenxacademy.com/duyurular/${announcement.slug}`
    }
  };

  const faqData = announcement.faqs || [];

  const faqStructuredData = faqData.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  return (
    <div className="announcement-detail">
      <Helmet>
        <title>{announcement.title} | ProgenX Duyurular</title>
        <meta name="description" content={announcement.excerpt} />
        <meta name="keywords" content={`progenx, ${announcement.tags.join(', ')}, ${announcement.category}, duyurular`} />
        <meta property="og:title" content={announcement.title} />
        <meta property="og:description" content={announcement.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://progenxacademy.com/duyurular/${announcement.slug}`} />
        {announcement.images && announcement.images.length > 0 && (
          <meta property="og:image" content={`https://progenxacademy.com${announcement.images[0]}`} />
        )}
        <meta property="article:published_time" content={announcement.date} />
        <meta property="article:author" content="ProgenX Academy" />
        <meta property="article:section" content={announcement.category} />
        <link rel="canonical" href={`https://progenxacademy.com/duyurular/${announcement.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        {faqStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(faqStructuredData)}
          </script>
        )}
      </Helmet>
      
      <Hero 
        title={announcement.title} 
        backgroundImage={duyurularHeroImage}
      />
      
      <section className="announcement-detail-content">
        <div className="container">
          <Link to="/duyurular" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} />
            Tüm Duyurulara Dön
          </Link>

          <article className="announcement-article">
            <div className="announcement-meta">
              <span className="announcement-category">
                <FontAwesomeIcon icon={faTag} /> {announcement.category}
              </span>
              <span className="announcement-date">
                <FontAwesomeIcon icon={faCalendarAlt} /> {announcement.date}
              </span>
            </div>

            <div 
              className="announcement-content"
              dangerouslySetInnerHTML={{ __html: announcement.content }} 
            />

            {announcement.images && announcement.images.length > 0 && (
              <div className="announcement-gallery">
                <ImageGallery images={announcement.images} />
              </div>
            )}

            {announcement.links && announcement.links.length > 0 && (
              <div className="announcement-links">
                <h3>İlgili Linkler</h3>
                <ul>
                  {announcement.links
                    .filter(link => {
                      // Show links with (Türkçe) or links without language indicator
                      return link.text.includes('(Türkçe)') || (!link.text.includes('(Türkçe)') && !link.text.includes('(English)'));
                    })
                    .map((link, index) => {
                      // Remove language indicator from text
                      const displayText = link.text.replace(' (Türkçe)', '').replace(' (English)', '');
                      return (
                        <li key={index}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer">
                            {displayText}
                          </a>
                        </li>
                      );
                    })}
                </ul>
              </div>
            )}

            {announcement.tags && announcement.tags.length > 0 && (
              <div className="announcement-tags">
                {announcement.tags.map((tag, index) => (
                  <span key={index} className="tag">#{tag}</span>
                ))}
              </div>
            )}

            {faqData.length > 0 && (
              <section className="announcement-faq">
                <h2>Sıkça Sorulan Sorular</h2>
                {faqData.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h3 className="faq-question">{faq.question}</h3>
                    <p className="faq-answer">{faq.answer}</p>
                  </div>
                ))}
              </section>
            )}
          </article>

          <div className="announcement-navigation">
            {previousAnnouncement && (
              <Link to={`/duyurular/${previousAnnouncement.slug}`} className="nav-link prev">
                <FontAwesomeIcon icon={faArrowLeft} />
                <div>
                  <span className="nav-label">Önceki</span>
                  <span className="nav-title">{previousAnnouncement.title}</span>
                </div>
              </Link>
            )}
            {nextAnnouncement && (
              <Link to={`/duyurular/${nextAnnouncement.slug}`} className="nav-link next">
                <div>
                  <span className="nav-label">Sonraki</span>
                  <span className="nav-title">{nextAnnouncement.title}</span>
                </div>
                <FontAwesomeIcon icon={faArrowLeft} style={{ transform: 'rotate(180deg)' }} />
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AnnouncementDetail;
